import type { CrystalIconKey } from './components/CrystalIcons';

export type GoalTheme =
  | 'Identity Building'
  | 'Bias Resilience'
  | 'Peer Connection'
  | 'Cultural Reconnection'
  | 'Advocacy in Care'
  | 'Daily Life & Independence'
  | 'Mental Wellness';

export interface MicroStep {
  id: string;
  label: string;
}

export interface GoalTemplate {
  id: string;
  title: string;
  theme: GoalTheme;
  description: string;
  microSteps: MicroStep[];
  linkedLibraryIds: string[];
  xpReward: number;
  emotionalIntensity: 1 | 2 | 3;
}

export const GOAL_TEMPLATES: GoalTemplate[] = [
  // ── Identity Building ────────────────────────────────────────────────
  {
    id: 'goal-identity-name-it',
    title: 'Name the identities that matter to me',
    theme: 'Identity Building',
    description: 'Getting clear on who you are — beyond your diagnosis — builds a stronger foundation for everything else.',
    microSteps: [
      { id: 'ms-1', label: 'List 3 identities that feel most true to you right now (e.g. Black, artist, daughter)' },
      { id: 'ms-2', label: 'Write one sentence about what each identity means to you' },
      { id: 'ms-3', label: 'Update them in your CareWeave profile' },
    ],
    linkedLibraryIds: ['ed_before_is_it_bad_enough_v1'],
    xpReward: 80,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-identity-values',
    title: 'Get clear on what I value most',
    theme: 'Identity Building',
    description: 'Knowing your values helps you make decisions, set boundaries, and resist pressure from systems that don\'t see you fully.',
    microSteps: [
      { id: 'ms-1', label: 'Pick 5 values from a list (e.g. honesty, creativity, community, rest, justice)' },
      { id: 'ms-2', label: 'Choose the 2 that feel most essential right now' },
      { id: 'ms-3', label: 'Note one situation where living by those values felt hard' },
    ],
    linkedLibraryIds: ['ed_after_debrief_normalize_v1'],
    xpReward: 80,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-identity-beyond-diagnosis',
    title: 'Build an identity beyond my diagnosis',
    theme: 'Identity Building',
    description: 'Sickle cell is part of your story — but it\'s not all of it. This goal helps you hold your whole self.',
    microSteps: [
      { id: 'ms-1', label: 'Write down 3 things you love or are good at that have nothing to do with being sick' },
      { id: 'ms-2', label: 'Tell one person something you\'re proud of (not health-related)' },
    ],
    linkedLibraryIds: ['ed_after_what_helped_micro_reflection_v1'],
    xpReward: 60,
    emotionalIntensity: 1,
  },

  // ── Bias Resilience ───────────────────────────────────────────────────
  {
    id: 'goal-bias-name-it',
    title: 'Learn to name bias when I experience it',
    theme: 'Bias Resilience',
    description: 'Recognizing discrimination as a system problem — not a personal flaw — is a skill that protects your sense of self.',
    microSteps: [
      { id: 'ms-1', label: 'Read one story in Articles about bias in care' },
      { id: 'ms-2', label: 'Log one past event — without blaming yourself' },
      { id: 'ms-3', label: 'Write one sentence that names it as a system issue, not your fault' },
    ],
    linkedLibraryIds: ['story_ed_too_calm_v1'],
    xpReward: 100,
    emotionalIntensity: 2,
  },
  {
    id: 'goal-bias-emotional-prep',
    title: 'Prepare emotionally before difficult care settings',
    theme: 'Bias Resilience',
    description: 'Having a ritual or anchor before going into a crisis visit or clinic can reduce harm from anticipated bias.',
    microSteps: [
      { id: 'ms-1', label: 'Identify one grounding strategy that helps you (e.g. breathing, music, a phrase)' },
      { id: 'ms-2', label: 'Practice it before your next appointment or crisis visit' },
    ],
    linkedLibraryIds: ['ed_during_waiting_grounding_v1'],
    xpReward: 70,
    emotionalIntensity: 2,
  },
  {
    id: 'goal-bias-debrief',
    title: 'Build a post-care debrief practice',
    theme: 'Bias Resilience',
    description: 'After a hard care experience, having a routine to process and release keeps the harm from accumulating.',
    microSteps: [
      { id: 'ms-1', label: 'Write down what happened after your next difficult care interaction' },
      { id: 'ms-2', label: 'Name one thing that was not your fault' },
      { id: 'ms-3', label: 'Do one thing kind for your body or mind afterward (rest, music, food you love)' },
    ],
    linkedLibraryIds: ['ed_after_debrief_normalize_v1'],
    xpReward: 90,
    emotionalIntensity: 2,
  },

  // ── Peer Connection ───────────────────────────────────────────────────
  {
    id: 'goal-peer-find-community',
    title: 'Find one person who gets it',
    theme: 'Peer Connection',
    description: 'Being understood by even one peer who shares your experience can change how you feel about your situation.',
    microSteps: [
      { id: 'ms-1', label: 'Think of one person in your life who has some understanding of chronic illness or discrimination' },
      { id: 'ms-2', label: 'Reach out and share one thing you\'re going through — as little or as much as you want' },
    ],
    linkedLibraryIds: ['ed_during_waiting_grounding_v1'],
    xpReward: 70,
    emotionalIntensity: 2,
  },
  {
    id: 'goal-peer-share-story',
    title: 'Share part of my story with someone I trust',
    theme: 'Peer Connection',
    description: 'Sharing — even a small piece — reduces isolation and helps others see your experience as real.',
    microSteps: [
      { id: 'ms-1', label: 'Decide what part of your story you feel ready to share' },
      { id: 'ms-2', label: 'Choose one trusted person to share it with (in person, text, or writing)' },
      { id: 'ms-3', label: 'Share it on your terms — in the way that feels safest for you' },
    ],
    linkedLibraryIds: ['story_ed_believed_and_documented_v1'],
    xpReward: 90,
    emotionalIntensity: 2,
  },

  // ── Cultural Reconnection ─────────────────────────────────────────────
  {
    id: 'goal-culture-ground-yourself',
    title: 'Reconnect with a cultural anchor',
    theme: 'Cultural Reconnection',
    description: 'Cultural traditions, music, food, or language can be a source of grounding when medical systems make you feel invisible.',
    microSteps: [
      { id: 'ms-1', label: 'Identify one cultural practice, food, or tradition that makes you feel at home in yourself' },
      { id: 'ms-2', label: 'Do it — or do a version of it — this week' },
    ],
    linkedLibraryIds: ['ed_after_what_helped_micro_reflection_v1'],
    xpReward: 60,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-culture-community-knowledge',
    title: 'Learn how my community has navigated healthcare',
    theme: 'Cultural Reconnection',
    description: 'Many racialized communities have developed wisdom and strategies for navigating hostile or dismissive systems. That knowledge belongs to you.',
    microSteps: [
      { id: 'ms-1', label: 'Ask a family member or elder how they\'ve handled difficult medical situations' },
      { id: 'ms-2', label: 'Read one community story in Articles' },
    ],
    linkedLibraryIds: ['story_ed_security_watch_v1'],
    xpReward: 70,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-culture-joy',
    title: 'Create a joy practice rooted in my culture',
    theme: 'Cultural Reconnection',
    description: 'Joy is resistance. Creating regular moments of culturally-rooted joy is an act of care for your whole self.',
    microSteps: [
      { id: 'ms-1', label: 'Name one music, art, or ritual practice that brings you joy and connects you to your culture' },
      { id: 'ms-2', label: 'Schedule time for it this week — even 15 minutes counts' },
      { id: 'ms-3', label: 'Notice how your body feels before and after' },
    ],
    linkedLibraryIds: ['ed_after_one_slider_checkin_v1'],
    xpReward: 80,
    emotionalIntensity: 1,
  },

  // ── Advocacy in Care ──────────────────────────────────────────────────
  {
    id: 'goal-advocacy-script',
    title: 'Build and practice my crisis advocacy script',
    theme: 'Advocacy in Care',
    description: 'Having your own words ready — before you\'re in crisis — means less cognitive labour when you need it most.',
    microSteps: [
      { id: 'ms-1', label: 'Open the Crisis Advocacy Script in Articles and customize it to your situation' },
      { id: 'ms-2', label: 'Read it aloud once so the words feel familiar' },
      { id: 'ms-3', label: 'Save or copy it so it\'s ready when you need it' },
    ],
    linkedLibraryIds: ['script_ed_drug_seeking_physician_v1'],
    xpReward: 90,
    emotionalIntensity: 2,
  },
  {
    id: 'goal-advocacy-rights',
    title: 'Know my rights as a patient',
    theme: 'Advocacy in Care',
    description: 'Understanding what you are entitled to as a patient — including the right to pain management — shifts the power dynamic.',
    microSteps: [
      { id: 'ms-1', label: 'Read the "Know Your Rights" article in Articles' },
      { id: 'ms-2', label: 'Identify one right you didn\'t fully know about' },
    ],
    linkedLibraryIds: ['ed_before_emergency_profile_intro_v1'],
    xpReward: 70,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-advocacy-documentation',
    title: 'Start documenting my care experiences',
    theme: 'Advocacy in Care',
    description: 'Your logged events are evidence. Documentation helps you spot patterns, advocate with clinicians, and hold systems accountable.',
    microSteps: [
      { id: 'ms-1', label: 'Log one past care event — even a brief one' },
      { id: 'ms-2', label: 'Visit Patterns to see what trends show up for you' },
      { id: 'ms-3', label: 'Set an intention to log your next care event when you feel ready' },
    ],
    linkedLibraryIds: ['ed_before_triage_overview_v1'],
    xpReward: 100,
    emotionalIntensity: 2,
  },

  // ── Daily Life & Independence ─────────────────────────────────────────
  {
    id: 'goal-indie-accommodation',
    title: 'Write my first accommodation letter',
    theme: 'Daily Life & Independence',
    description: 'Having accommodations in writing — before a crisis — protects your grades, your job, and your wellbeing. This goal walks you through the process.',
    microSteps: [
      { id: 'ms-1', label: 'Use the accommodation letter starter in the School journey to draft your letter' },
      { id: 'ms-2', label: 'Identify one specific accommodation you want (e.g. extended deadlines, absence allowance)' },
      { id: 'ms-3', label: 'Email or deliver the letter to student services or your coordinator' },
    ],
    linkedLibraryIds: ['ed_before_emergency_profile_intro_v1'],
    xpReward: 100,
    emotionalIntensity: 2,
  },
  {
    id: 'goal-indie-emergency-plan',
    title: 'Build my emergency medication plan',
    theme: 'Daily Life & Independence',
    description: 'When you\'re managing SCD on your own, having an emergency plan means less panic and faster care when a crisis hits.',
    microSteps: [
      { id: 'ms-1', label: 'List your medications, doses, and prescribing doctors in one place' },
      { id: 'ms-2', label: 'Build a 3-day emergency medication supply if possible' },
      { id: 'ms-3', label: 'Share your plan with one person who could help in an emergency' },
    ],
    linkedLibraryIds: ['ed_before_emergency_profile_intro_v1'],
    xpReward: 90,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-indie-transition-prep',
    title: 'Prepare for my transition to adult care',
    theme: 'Daily Life & Independence',
    description: 'Transition to adult care is one of the highest-risk periods for youth with SCD. Starting early — even if it\'s still years away — gives you the best outcome.',
    microSteps: [
      { id: 'ms-1', label: 'Request a copy of your care history from your current team' },
      { id: 'ms-2', label: 'Learn your SCD genotype and current treatment plan in your own words' },
      { id: 'ms-3', label: 'Ask your pediatric team about the adult care clinic they\'re connected to' },
    ],
    linkedLibraryIds: ['ed_before_triage_overview_v1'],
    xpReward: 110,
    emotionalIntensity: 2,
  },
  {
    id: 'goal-indie-daily-routine',
    title: 'Build a sustainable daily routine with SCD',
    theme: 'Daily Life & Independence',
    description: 'Consistent sleep, hydration, warmth, and stress management don\'t cure SCD — but they reduce flare frequency. Building these habits is an act of care.',
    microSteps: [
      { id: 'ms-1', label: 'Identify your two most common crisis triggers (e.g. cold, dehydration, exhaustion)' },
      { id: 'ms-2', label: 'Build one daily habit that directly addresses your top trigger' },
      { id: 'ms-3', label: 'Track for 7 days — notice any difference' },
    ],
    linkedLibraryIds: ['ed_after_what_helped_micro_reflection_v1'],
    xpReward: 80,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-indie-find-specialist',
    title: 'Find my local SCD specialist',
    theme: 'Daily Life & Independence',
    description: 'Having a named specialist — not just emergency visits — is one of the biggest factors in better outcomes. This goal helps you identify and connect with yours.',
    microSteps: [
      { id: 'ms-1', label: 'Ask your current team who the adult hematology contact is at your hospital' },
      { id: 'ms-2', label: 'Look up the SCD Society of Canada for a clinic directory' },
      { id: 'ms-3', label: 'Book an appointment or get a referral in motion' },
    ],
    linkedLibraryIds: ['ed_before_triage_overview_v1'],
    xpReward: 120,
    emotionalIntensity: 1,
  },

  // ── Mental Wellness ───────────────────────────────────────────────────
  {
    id: 'goal-mh-recognize',
    title: 'Recognize when I\'m struggling beyond just SCD',
    theme: 'Mental Wellness',
    description: 'Pain and mental health are deeply connected. Learning to recognize when you need mental health support — not just pain management — is a skill that matters.',
    microSteps: [
      { id: 'ms-1', label: 'Review the "Signs You Might Be Struggling" article in the Mental Health journey' },
      { id: 'ms-2', label: 'Write down one feeling or pattern you\'ve noticed in yourself recently' },
      { id: 'ms-3', label: 'Name it without judgment — "I\'ve been feeling [X] and that makes sense given [context]"' },
    ],
    linkedLibraryIds: ['ed_during_waiting_grounding_v1'],
    xpReward: 80,
    emotionalIntensity: 2,
  },
  {
    id: 'goal-mh-daily-practice',
    title: 'Practice one grounding technique daily',
    theme: 'Mental Wellness',
    description: 'Grounding practices — breathing, body scans, movement — reduce the baseline stress that makes pain worse. A daily practice doesn\'t have to be long.',
    microSteps: [
      { id: 'ms-1', label: 'Pick one grounding technique from Articles (box breathing, 5-4-3-2-1, etc.)' },
      { id: 'ms-2', label: 'Do it once today — even for 2 minutes' },
      { id: 'ms-3', label: 'Set a daily reminder and do it for 7 consecutive days' },
    ],
    linkedLibraryIds: ['ed_during_waiting_grounding_v1'],
    xpReward: 90,
    emotionalIntensity: 1,
  },
  {
    id: 'goal-mh-tell-someone',
    title: 'Talk to someone about how SCD affects my mental health',
    theme: 'Mental Wellness',
    description: 'You don\'t have to carry this alone. Saying out loud that managing SCD is also affecting your mood or anxiety is an act of courage — and often a turning point.',
    microSteps: [
      { id: 'ms-1', label: 'Decide who you want to tell — a friend, family member, or care provider' },
      { id: 'ms-2', label: 'Use the "What to Say" script from the Mental Health journey as a starting point' },
      { id: 'ms-3', label: 'Share it — on your terms, in the way that feels safest' },
    ],
    linkedLibraryIds: ['story_ed_believed_and_documented_v1'],
    xpReward: 100,
    emotionalIntensity: 3,
  },
];

export const GOAL_THEMES: GoalTheme[] = [
  'Identity Building',
  'Bias Resilience',
  'Peer Connection',
  'Cultural Reconnection',
  'Advocacy in Care',
  'Daily Life & Independence',
  'Mental Wellness',
];

// Cultural Reconnection's globe-style emoji has no clean Crystal-registry match;
// iconKey omitted there until a globe icon is designed in Tracks A–E.
export const THEME_META: Record<GoalTheme, { icon: string; iconKey?: CrystalIconKey; color: string; desc: string }> = {
  'Identity Building': {
    icon: '🌱',
    iconKey: 'growth-sprout',
    color: 'var(--color-success)',
    desc: 'Explore and strengthen who you are beyond your diagnosis',
  },
  'Bias Resilience': {
    icon: '🛡️',
    iconKey: 'shield',
    color: '#8b6fb0',
    desc: 'Build skills to recognize, name, and recover from discrimination',
  },
  'Peer Connection': {
    icon: '🤝',
    iconKey: 'handshake',
    color: '#ec4899',
    desc: 'Find community and share your experience with people who understand',
  },
  'Cultural Reconnection': {
    icon: '🌍',
    color: 'var(--color-warning)',
    desc: 'Draw on cultural wisdom, joy, and belonging as sources of strength',
  },
  'Advocacy in Care': {
    icon: '✊',
    iconKey: 'solidarity',
    color: '#06b6d4',
    desc: 'Develop the tools and confidence to advocate for yourself in care settings',
  },
  'Daily Life & Independence': {
    icon: '🏠',
    iconKey: 'journey-independence',
    color: '#f97316',
    desc: 'Build routines, access supports, and manage SCD on your own terms',
  },
  'Mental Wellness': {
    icon: '🧘',
    iconKey: 'grounding',
    color: '#8b6fb0',
    desc: 'Recognize, name, and actively support your mental health alongside SCD',
  },
};
